// ============================================================================
// 4. PARSING - Parse raw RPC responses
// ============================================================================

import { decodeFunctionResult, decodeAbiParameters } from 'viem'
import { PreparedCall, RawRpcBatch, MulticallRpcBatch } from './rpcCall'

export interface RawRpcResponse {
  jsonrpc: '2.0'
  id: number
  result?: string
  error?: {
    code: number
    message: string
  }
}

export function parseRawRpcResponses(
  responses: RawRpcResponse[],
  callMetadata: PreparedCall[],
  allowFailure: boolean = true,
): any[] {
  // Sort responses by ID to match original call order
  const sortedResponses = [...responses].sort((a, b) => a.id - b.id)

  return sortedResponses.map((response, idx) => {
    const metadata = callMetadata[idx]

    // Handle error responses
    if (response.error || !response.result) {
      if (!allowFailure) {
        throw new Error(
          `RPC call failed for ${metadata.functionName}: ${
            response.error?.message || 'No result'
          }`,
        )
      }
      return '0x'
    }

    // Handle successful responses
    try {
      const decoded = decodeFunctionResult({
        abi: metadata.abi,
        functionName: metadata.functionName,
        data: response.result as `0x${string}`,
      })

      return decoded
    } catch (error) {
      if (!allowFailure) {
        throw new Error(
          `Failed to decode result for ${metadata.functionName}: ${error}`,
        )
      }
      return '0x'
    }
  })
}

export function parseRawRpcBatchResponses(
  batches: RawRpcBatch[],
  batchResponses: RawRpcResponse[][],
  allowFailure: boolean = true,
): any[] {
  const allResults: any[] = []

  batches.forEach((batch, batchIdx) => {
    const responses = batchResponses[batchIdx]
    const parsed = parseRawRpcResponses(
      responses,
      batch.callMetadata,
      allowFailure,
    )
    allResults.push(...parsed)
  })

  return allResults
}

/**
 * Parses multicall3 aggregate3 responses
 * The response contains an array of {success, returnData} tuples
 * Each returnData needs to be decoded using the original call's ABI
 *
 * `permanentFailures`, when supplied, is filled with the indices of calls that
 * failed DETERMINISTICALLY. This path can tell them apart with certainty, which
 * the viem path can only infer: if the batch response itself came back, the
 * transport worked, so a `success: false` entry inside it is a revert — the
 * chain's answer, not a lost read. Only a batch-level error is a lost read.
 *
 * The distinction matters downstream: a cross-margin lender voids its whole set
 * on a lost read, and without this a single always-reverting market would void
 * a perfectly good position on every request.
 */
export function parseMulticallRpcResponses(
  responses: RawRpcResponse[],
  batches: MulticallRpcBatch[],
  allowFailure: boolean = true,
  permanentFailures?: Set<number>,
): any[] {
  const allResults: any[] = []

  // Sort responses by ID to match batch order
  const sortedResponses = [...responses].sort((a, b) => a.id - b.id)

  sortedResponses.forEach((response, batchIdx) => {
    const batch = batches[batchIdx]

    // Handle RPC-level error
    if (response.error || !response.result) {
      if (!allowFailure) {
        throw new Error(
          `Multicall RPC failed: ${response.error?.message || 'No result'}`,
        )
      }
      // Return '0x' for all calls in this batch
      batch.callMetadata.forEach(() => allResults.push('0x'))
      return
    }

    // Decode the aggregate3 response - it returns an array of (success, returnData) tuples
    try {
      const decoded = decodeAbiParameters(
        [
          {
            type: 'tuple[]',
            components: [
              { name: 'success', type: 'bool' },
              { name: 'returnData', type: 'bytes' },
            ],
          },
        ],
        response.result as `0x${string}`,
      )[0] as { success: boolean; returnData: `0x${string}` }[]

      // Now decode each individual call's returnData
      decoded.forEach((result, idx) => {
        const metadata = batch.callMetadata[idx]

        if (!result.success || result.returnData === '0x') {
          if (!allowFailure) {
            throw new Error(`Call failed for ${metadata.functionName}`)
          }
          // The batch was served, so this is the chain's answer: a revert.
          permanentFailures?.add(allResults.length)
          allResults.push('0x')
          return
        }

        try {
          const decodedResult = decodeFunctionResult({
            abi: metadata.abi,
            functionName: metadata.functionName,
            data: result.returnData,
          })
          allResults.push(decodedResult)
        } catch (error) {
          if (!allowFailure) {
            throw new Error(
              `Failed to decode result for ${metadata.functionName}: ${error}`,
            )
          }
          // Undecodable data is equally stable — re-reading returns the same.
          permanentFailures?.add(allResults.length)
          allResults.push('0x')
        }
      })
    } catch (error) {
      if (!allowFailure) {
        throw new Error(`Failed to decode multicall response: ${error}`)
      }
      // Return '0x' for all calls in this batch
      batch.callMetadata.forEach(() => allResults.push('0x'))
    }
  })

  return allResults
}
